import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { fetchAuditLog } from '../../api/admin';
import { AdminDataTable, AdminPagination, Column } from '../../components/admin';

const AdminAuditLog: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const pageSize = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (actionFilter) params.action = actionFilter;
    fetchAuditLog(params)
      .then((res) => { setLogs(res.data.results); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const columns: Column[] = [
    { key: 'id', header: 'ID', width: '50px' },
    {
      key: 'action', header: 'Action',
      render: (row) => (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
          {row.action}
        </span>
      ),
    },
    { key: 'target_type', header: 'Target', render: (row) => row.target_type ? `${row.target_type} #${row.target_id}` : '-' },
    {
      key: 'details', header: 'Details', width: '300px',
      render: (row) => (
        <span className="text-xs truncate max-w-[300px] block" style={{ color: colors.grayScale500 }}>
          {row.details ? JSON.stringify(row.details) : '-'}
        </span>
      ),
    },
    { key: 'ip_address', header: 'IP', width: '120px', render: (row) => row.ip_address || '-' },
    { key: 'admin_user_id', header: 'Admin', width: '60px' },
    {
      key: 'created_at', header: 'Time',
      render: (row) => {
        const d = new Date(row.created_at);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      },
    },
  ];

  const actionOptions = [
    '', 'update_user', 'delete_user', 'blacklist_user', 'unblacklist_user',
    'update_subscription', 'refund', 'update_booking', 'verify_expert',
    'wallet_adjust', 'reset_queries', 'send_email', 'add_blacklisted_ip',
    'remove_blacklisted_ip', 'update_ticket',
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Audit Log</h1>

      <div className="mb-4">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm outline-none"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
        >
          {actionOptions.map((a) => (
            <option key={a} value={a}>{a || 'All Actions'}</option>
          ))}
        </select>
      </div>

      <AdminDataTable columns={columns} data={logs} loading={loading} emptyMessage="No audit logs" />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />
    </div>
  );
};

export default AdminAuditLog;
